import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { MessageCircle, Mail, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

export default function NotificationSettings() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/notification-settings");
            setCfg(r.data);
        } catch (e) { console.warn(e); }
    };

    useEffect(() => { load(); }, []);

    if (!cfg) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;

    const toggleChannel = (ch) => {
        const has = cfg.channels.includes(ch);
        setCfg({ ...cfg, channels: has ? cfg.channels.filter(x => x !== ch) : [...cfg.channels, ch] });
    };
    const toggleStatus = (st) => {
        const has = cfg.statuses.includes(st);
        setCfg({ ...cfg, statuses: has ? cfg.statuses.filter(x => x !== st) : [...cfg.statuses, st] });
    };
    const updateTemplate = (st, val) => {
        setCfg({ ...cfg, templates: { ...cfg.templates, [st]: val } });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/notification-settings", {
                enabled: cfg.enabled, channels: cfg.channels, statuses: cfg.statuses, templates: cfg.templates,
            });
            toast.success("Configuración guardada");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4 max-w-4xl" data-testid="notification-settings-page">
            <div>
                <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                    <MessageCircle className="w-6 h-6 text-emerald-600" /> Notificaciones automáticas
                    <HelpTip text="Cuando un servicio cambia de estatus, el sistema enviará automáticamente WhatsApp/SMS/Email al cliente según las plantillas configuradas aquí. Requiere Twilio (WA/SMS) y Resend (Email) configurados en Integraciones." />
                </h2>
                <p className="text-xs text-slate-500 mt-1">Configura plantillas y canales para que el cliente se entere del progreso del servicio sin que cabina tenga que apretar nada.</p>
            </div>

            <div className="fm-card p-5 space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-4 h-4" data-testid="notify-enabled" />
                    <div>
                        <div className="font-bold text-sm">Activar notificaciones automáticas</div>
                        <div className="text-xs text-slate-500">Si está OFF, las notificaciones sólo se envían cuando das clic manualmente.</div>
                    </div>
                </label>

                <div className="border-t border-slate-200 pt-4">
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-2">Canales</div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { v: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "emerald" },
                            { v: "sms", label: "SMS", icon: MessageCircle, color: "blue" },
                            { v: "email", label: "Email", icon: Mail, color: "purple" },
                        ].map((c) => {
                            const Icon = c.icon;
                            const active = cfg.channels.includes(c.v);
                            return (
                                <button
                                    key={c.v}
                                    onClick={() => toggleChannel(c.v)}
                                    className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition ${active ? `bg-${c.color}-600 text-white` : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                    data-testid={`channel-${c.v}`}
                                >
                                    <Icon className="w-3.5 h-3.5" /> {c.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-2 flex items-center gap-1">
                        Estatus que disparan notificación
                        <HelpTip text="Selecciona en qué cambios de estatus quieres avisar al cliente. Lo recomendado: unidad_asignada, arribo_origen, entrega_paciente, servicio_finalizado." />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {cfg.available_statuses.map((st) => {
                            const active = cfg.statuses.includes(st);
                            return (
                                <label key={st} className={`flex items-start gap-2 p-2 rounded-sm border cursor-pointer ${active ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                                    <input type="checkbox" checked={active} onChange={() => toggleStatus(st)} className="mt-0.5" data-testid={`status-toggle-${st}`} />
                                    <span className="text-xs">
                                        <div className="font-bold">{cfg.status_labels?.[st] || st}</div>
                                        <div className="font-mono text-[10px] text-slate-500">{st}</div>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-2 flex items-center gap-1">
                        Plantillas por estatus
                        <HelpTip text="Variables disponibles: {{folio}}, {{client}}, {{patient}}, {{unit}}. Se reemplazan automáticamente al enviar." />
                    </div>
                    <div className="space-y-2">
                        {cfg.available_statuses.map((st) => (
                            <div key={st} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 items-start">
                                <div className="text-xs">
                                    <div className="font-bold">{cfg.status_labels?.[st] || st}</div>
                                    <div className="font-mono text-[10px] text-slate-500">{st}</div>
                                </div>
                                <textarea
                                    rows={2}
                                    value={cfg.templates[st] || ""}
                                    onChange={(e) => updateTemplate(st, e.target.value)}
                                    className="fm-input text-xs font-mono"
                                    placeholder="Mensaje al cliente…"
                                    data-testid={`tpl-${st}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border-t border-slate-200 pt-4 flex flex-col sm:flex-row justify-end gap-2">
                    <button onClick={load} className="fm-btn-outline" data-testid="reload-btn">
                        <RotateCcw className="w-3.5 h-3.5" /> Recargar
                    </button>
                    <button onClick={save} disabled={saving} className="fm-btn-accent" data-testid="save-notify-btn">
                        <Save className="w-3.5 h-3.5" /> {saving ? "Guardando..." : "Guardar configuración"}
                    </button>
                </div>
            </div>

            <div className="fm-card p-4 bg-amber-50 border-l-4 border-l-amber-400">
                <div className="text-sm font-bold text-amber-900">⚠️ Recordatorio</div>
                <div className="text-xs text-amber-900 mt-1">
                    Para que las notificaciones se envíen en producción, debes configurar las claves en <a href="/integrations" className="underline font-bold">Integraciones</a>:
                    Twilio (WhatsApp + SMS) y Resend (Email). Si no están configuradas, los envíos quedan en modo MOCK (se registran pero no salen del sistema).
                </div>
            </div>
        </div>
    );
}
